import type { Graph, Edge } from '@antv/x6';

/**
 * 数据流可视化：
 * 在边上添加动画流动的令牌，模拟数据移动。
 */

const FLOW_ANIMATION_CLASS = 'opm-flow-token';

/**
 * 启动图中所有边的数据流动画。
 * 每条边都会应用 stroke-dasharray 动画以产生流动效果。
 */
export function startDataFlow(graph: Graph): void {
  const edges = graph.getEdges();
  edges.forEach((edge) => {
    applyFlowAnimation(edge);
  });
}

/**
 * 停止所有边的数据流动画。
 */
export function stopDataFlow(graph: Graph): void {
  const edges = graph.getEdges();
  edges.forEach((edge) => {
    removeFlowAnimation(edge);
  });
}

/**
 * 对单条边应用流动动画。
 */
export function applyFlowAnimation(edge: Edge): void {
  const line = edge.getAttrByPath('line') || {};
  edge.setAttrs({
    line: {
      ...line,
      strokeDasharray: '8 4',
      style: {
        animation: 'opm-flow-dash 1s linear infinite',
      },
      stroke: '#1890ff',
      strokeWidth: 2.5,
    },
  });
  // 将动画边置于 SVG 顶层，避免被其他边遮挡
  edge.toFront();
  edge.setData({ isFlowing: true });
}

/**
 * 移除单条边的流动动画。
 */
export function removeFlowAnimation(edge: Edge): void {
  edge.setAttrs({
    line: {
      strokeDasharray: '',
      style: {
        animation: '',
      },
      stroke: '#595959',
      strokeWidth: 1.8,
    },
  });
  edge.setData({ isFlowing: false });
}

/**
 * 使节点产生脉冲效果，以指示数据流活动。
 */
export function pulseNode(graph: Graph, nodeId: string, duration = 1000): void {
  const node = graph.getCellById(nodeId);
  if (!node || !node.isNode()) return;

  node.setData({ active: true });
  setTimeout(() => {
    node.setData({ active: false });
  }, duration);
}

/**
 * 动画展示数据沿路径流动：源节点 → 边 → 目标节点
 */
export function animateFlowPath(
  graph: Graph,
  edgeId: string,
  duration = 1200,
): Promise<void> {
  return new Promise((resolve) => {
    const edge = graph.getCellById(edgeId) as Edge | undefined;
    if (!edge || !edge.isEdge()) {
      resolve();
      return;
    }

    // 高亮边
    applyFlowAnimation(edge);

    // 脉冲源节点
    const sourceId = edge.getSourceCellId();
    if (sourceId) pulseNode(graph, sourceId, duration);

    // 延迟后脉冲目标节点
    setTimeout(() => {
      const targetId = edge.getTargetCellId();
      if (targetId) pulseNode(graph, targetId, duration);
    }, duration * 0.4);

    // 动画结束后清理
    setTimeout(() => {
      resolve();
    }, duration);
  });
}
